/** @jsxImportSource @emotion/react */
// see - https://www.figma.com/file/pv2HV6DTSKY612VWeYWYdL/CE22-Design-System?node-id=44%3A42
import type { PropsWithChildren, ReactNode } from 'react';
import React, { useMemo } from 'react';
import FormControlLabel from '@mui/material/FormControlLabel';
import type { CheckboxProps } from '@mui/material/Checkbox';
import CheckboxMUI from '@mui/material/Checkbox';
import type { SxProps } from '@mui/material';
import Typography, { TypographyVariant } from '../Typography';
import type { ITheme } from '../../styles/theme';
import { ElementZIndexOrdering } from '../../utils/element-z-iIndex-ordering';
import type { IPropsWithTestId } from '../../../constants/data-element-tracking-ids';
import {
  ELEMENT_ID_DATA_LOG_ATTRIBUTE,
  ELEMENT_ID_DATA_TEST_ATTRIBUTE,
} from '../../../constants/data-element-tracking-ids';
import { BaseErrorMessage } from '../Input/InputControlWrapper';

interface IProps extends IPropsWithTestId, CheckboxProps {
  label: ReactNode;
  error?: ReactNode;
  disabled?: boolean;
  isCheckBoxAtTop?: boolean;
  variant: 'default' | 'primary' | undefined;
  theme: ITheme;
  checked: boolean;
  onChange?: (_: React.ChangeEvent<HTMLInputElement>, checked: boolean) => void;
  additionalStyles?: SxProps;
}

const Checkbox: React.FC<PropsWithChildren<IProps>> = ({
  label,
  error,
  disabled = false,
  isCheckBoxAtTop = true,
  theme,
  variant = 'default',
  checked,
  onChange,
  dataElementId,
  additionalStyles = {},
  ...props
}) => {
  const styles = useMemo(
    () => ({
      default: {
        color: theme.colors.secondary,
        '&.Mui-checked, &.MuiCheckbox-indeterminate': {
          color: theme.colors.secondary,
        },
      },
      primary: {
        color: theme.colors.primary,
        '&.Mui-checked, &.MuiCheckbox-indeterminate': {
          color: theme.colors.primary,
        },
      },
      error: {
        color: theme.colors.negative,
        '&.Mui-checked, &.MuiCheckbox-indeterminate': {
          color: theme.colors.negative,
        },
      },
    }),
    [theme.colors],
  );

  const checkboxStyles = useMemo(
    () => ({
      '& .MuiCheckbox-root': {
        paddingRight: '8px',
      },
      '& .MuiSvgIcon-root': {
        fontSize: 17,
      },
      '&.Mui-disabled': {
        color: theme.colors.grayscale.lightGrey,
        '&::before': {
          content: "''",
          color: 'black',
          width: '12px',
          height: '12px',
          borderRadius: '4px',
          backgroundColor: theme.colors.grayscale.inactiveGrey2,
          position: 'absolute',
          zIndex: ElementZIndexOrdering.HIDDEN_ELEMENT,
        },
        '&.Mui-checked, &.MuiCheckbox-indeterminate': {
          '&::before': {
            backgroundColor: theme.colors.grayscale.inactiveGrey1,
          },
        },
      },
    }),
    [theme.colors],
  );

  return (
    <>
      <FormControlLabel
        sx={{
          ...(isCheckBoxAtTop
            ? {
              alignItems: 'flex-start',
              '.MuiCheckbox-root': { paddingTop: '4px' },
            }
            : {}),
          ...additionalStyles,
        }}
        control={(
          <CheckboxMUI
            sx={[styles[error ? 'error' : variant], checkboxStyles]}
            checked={checked}
            onChange={onChange}
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...{
              [ELEMENT_ID_DATA_TEST_ATTRIBUTE]: `${dataElementId}-checkbox`,
            }}
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...props}
          />
        )}
        label={(
          <Typography
            variant={TypographyVariant.MEDIUM_BODY_REGULAR}
            theme={theme}
            color={
              disabled
                ? theme.colors.grayscale.inactiveGrey1
                : theme.colors.secondary
            }
            dataElementId={`${dataElementId}-label`}
          >
            {label}
          </Typography>
        )}
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...{
          [ELEMENT_ID_DATA_LOG_ATTRIBUTE]: dataElementId,
          [ELEMENT_ID_DATA_TEST_ATTRIBUTE]: dataElementId,
        }}
      />
      {!!error && (
        <BaseErrorMessage
          theme={theme}
          dataElementId={`${dataElementId}-error`}
        >
          {error}
        </BaseErrorMessage>
      )}
    </>
  );
};

export default Checkbox;
